/**
 * Provider Dashboard Page
 * Shows submission stats, recent activity, and quick links
 */

import { authenticatedFetch } from "@lumi/data";
import { ClipboardList, FilePlus, ScrollText } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { ACTION_LABELS, formatDateTime, getActionBadgeVariant } from "../../../shared/components/audit";
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner";
import { StatCard } from "../../../shared/components/StatCard";
import { Badge } from "../../../shared/components/ui/badge";
import { useCascadeFadeIn } from "../../../shared/hooks/useCascadeFadeIn";

interface DashboardStats {
  total: number;
  drafts: number;
  pending: number;
  approved: number;
  rejected: number;
  published: number;
}

interface RecentActivity {
  id: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
  metadata: Record<string, unknown> | null;
}

interface ProviderProfile {
  displayName: string;
  slug: string;
  verified: boolean;
}

interface DashboardResponse {
  stats: DashboardStats;
  recentActivity: RecentActivity[];
  profile: ProviderProfile | null;
}

interface QuickLinkCardProps {
  readonly to: string;
  readonly title: string;
  readonly description: string;
  readonly icon: React.ReactNode;
  readonly index: number;
}

function QuickLinkCard({ to, title, description, icon, index }: QuickLinkCardProps) {
  const { ref, isVisible } = useCascadeFadeIn<HTMLAnchorElement>({
    index,
    threshold: 0.1,
    staggerDelay: 100,
  });

  return (
    <Link
      ref={ref}
      to={to}
      className={`group bg-neutral-900/0 border border-neutral-800 rounded-lg p-6 hover:border-neutral-700 transition-all duration-500 flex flex-col ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-white group-hover:text-lumi-accent transition-colors">{title}</h2>
        <div className="w-6 h-6 text-neutral-400 group-hover:text-lumi-accent transition-colors">{icon}</div>
      </div>
      <p className="text-sm text-neutral-400 flex-1">{description}</p>
      <div className="mt-4 text-sm text-lumi-accent group-hover:underline">View {title} →</div>
    </Link>
  );
}

interface ActivityItemProps {
  readonly activity: RecentActivity;
  readonly index: number;
}

function ActivityItem({ activity, index }: ActivityItemProps) {
  const { ref, isVisible } = useCascadeFadeIn<HTMLDivElement>({
    index,
    threshold: 0.1,
    staggerDelay: 50,
  });

  return (
    <div
      ref={ref}
      className={`flex items-center justify-between py-3 border-b border-neutral-800 last:border-0 transition-all duration-500 ${
        isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
      }`}
    >
      <div className="flex items-center gap-3">
        <Badge variant={getActionBadgeVariant(activity.action)} className="font-medium">
          {ACTION_LABELS[activity.action] || activity.action}
        </Badge>
        {activity.entityType && <span className="text-sm text-neutral-400 capitalize">{activity.entityType}</span>}
      </div>
      <span className="text-xs text-neutral-500">{formatDateTime(activity.createdAt)}</span>
    </div>
  );
}

/**
 * Provider Dashboard - Landing page with stats and quick links
 */
// eslint-disable-next-line max-lines-per-function
export function ProviderDashboard(): ReactElement {
  const { accessToken, refreshAuth, user } = useAuth();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!accessToken) {
        setError("Not authenticated");
        setLoading(false);
        return;
      }

      try {
        const result = await authenticatedFetch<DashboardResponse>(
          `${API_BASE}/v1/provider/dashboard`,
          { method: "GET" },
          accessToken,
          createRefreshCallback(refreshAuth),
        );
        setData(result);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load dashboard";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    void fetchDashboard();
  }, [accessToken, refreshAuth, API_BASE]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-8 text-destructive-foreground">Error: {error}</div>;
  }

  const stats = data?.stats ?? {
    total: 0,
    drafts: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    published: 0,
  };

  const displayName = data?.profile?.displayName || user?.email?.split("@")[0] || "Provider";

  return (
    <div className="space-y-8 py-8">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-white mb-2">Welcome back, {displayName}</h1>
        <p className="text-sm text-neutral-400 pb-6">Manage your content submissions and track their status</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="Total" value={stats.total} />
        <StatCard label="Drafts" value={stats.drafts} />
        <StatCard
          label="Pending Review"
          value={stats.pending}
          color={stats.pending > 0 ? "text-yellow-500" : "text-white"}
        />
        <StatCard
          label="Approved"
          value={stats.approved}
          color={stats.approved > 0 ? "text-green-500" : "text-white"}
        />
        <StatCard label="Rejected" value={stats.rejected} color={stats.rejected > 0 ? "text-red-500" : "text-white"} />
        <StatCard
          label="Published"
          value={stats.published}
          color={stats.published > 0 ? "text-green-500" : "text-white"}
        />
      </div>

      {/* Quick Links Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <QuickLinkCard
          to="/provider/new"
          title="New Submission"
          description="Submit a new film or video for review and publication"
          icon={<FilePlus className="h-6 w-6" />}
          index={0}
        />
        <QuickLinkCard
          to="/provider/submissions"
          title="Submissions"
          description="View and manage all your content submissions"
          icon={<ClipboardList className="h-6 w-6" />}
          index={1}
        />
        <QuickLinkCard
          to="/provider/audit-log"
          title="Activity Log"
          description="View your recent actions and submission history"
          icon={<ScrollText className="h-6 w-6" />}
          index={2}
        />
      </div>

      {/* Recent Activity */}
      {data?.recentActivity && data.recentActivity.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Recent Activity</h2>
            <Link to="/provider/audit-log" className="text-sm text-lumi-accent hover:underline">
              View all →
            </Link>
          </div>
          <div className="bg-neutral-900/30 border border-neutral-800 rounded-lg p-4">
            {data.recentActivity.map((activity, index) => (
              <ActivityItem key={activity.id} activity={activity} index={index} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state for no activity */}
      {(!data?.recentActivity || data.recentActivity.length === 0) && (
        <div className="bg-neutral-900/30 border border-neutral-800 rounded-lg p-8 text-center">
          <ScrollText className="h-12 w-12 text-neutral-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No recent activity</h3>
          <p className="text-sm text-neutral-400 mb-4">Start by creating your first submission</p>
          <Link
            to="/provider/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-lumi-accent text-black font-medium rounded-md hover:bg-lumi-accent/90 transition-colors"
          >
            <FilePlus className="h-4 w-4" />
            New Submission
          </Link>
        </div>
      )}
    </div>
  );
}
